import {
  Inject,
  Injectable,
  Renderer2,
  RendererFactory2,
  RendererStyleFlags2,
  DOCUMENT,
  inject,
  PLATFORM_ID,
  TransferState,
  REQUEST,
} from '@angular/core';
import { Theme, ThemeColorsPayload, defaultTheme } from '@app/theme/theme';
import { API_URL } from '@core/tokens/api.token';
import { HttpClient } from '@angular/common/http';
import { Observable, of, TimeoutError } from 'rxjs';
import { catchError, tap, timeout, shareReplay, finalize } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { DomainResolverService } from '@services/domain-resolver.service';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { TRANSFER_STATE_KEYS } from '@app/core/constants/transfer-state-keys';

interface ThemeCache {
  data: Theme;
  expires: number;
}

const serverThemeCache = new Map<string, ThemeCache>();
const serverPendingRequests = new Map<string, Observable<Theme>>();
const CACHE_TTL = 12 * 60 * 60 * 1000; // 12 hours

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly cookieStorageService = inject(SsrCookieService);
  private readonly domainResolverService = inject(DomainResolverService);
  private readonly transferState = inject(TransferState);
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly rendererFactory = inject(RendererFactory2);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly request = inject(REQUEST, { optional: true });

  fetchTheme() {

    if (this.isBuildEnvironment()) {
      this.setColorsValues(defaultTheme);
      return of(defaultTheme);
    }

    if (!this.isBrowser) {
      const currentDomain = this.domainResolverService.getHostname();

      // Check if request is already in progress FIRST (better concurrency)
      const pendingRequest = serverPendingRequests.get(currentDomain);
      if (pendingRequest) {
        return pendingRequest;
      }

      // Then check cache
      const cached = serverThemeCache.get(currentDomain);
      if (cached && Date.now() < cached.expires) {
        this.setColorsValues(cached.data);
        this.transferState.set(TRANSFER_STATE_KEYS.THEME_DATA, cached.data);
        return of(cached.data);
      }

      const path = `${this.apiUrl}/theme`;
      const request$ = this.http.get<Theme>(path).pipe(
        tap(res => {
          // Cache the theme data
          serverThemeCache.set(currentDomain, { data: res, expires: Date.now() + CACHE_TTL });
          this.setColorsValues(res);
          this.transferState.set(TRANSFER_STATE_KEYS.THEME_DATA, res);
        }),
        catchError(error => {
          const errorType = error instanceof TimeoutError ? 'Timeout' : 'Error';
          console.warn(`[ThemeService] ${errorType} fetching theme for ${currentDomain}:`, error.message);
          
          // Check for stale cache
          const staleCache = serverThemeCache.get(currentDomain);
          if (staleCache) {
            console.log(`[ThemeService] Using stale cached theme for ${currentDomain}`);
            this.setColorsValues(staleCache.data);
            this.transferState.set(TRANSFER_STATE_KEYS.THEME_DATA, staleCache.data);
            return of(staleCache.data);
          }
          
          // Fall back to default theme
          this.setColorsValues(defaultTheme);
          this.transferState.set(TRANSFER_STATE_KEYS.THEME_DATA, defaultTheme);
          return of(defaultTheme);
        }),
        finalize(() => {
          // Always cleanup pending request
          serverPendingRequests.delete(currentDomain);
        }),
        shareReplay({ bufferSize: 1, refCount: true }) // Share with automatic cleanup
      );

      serverPendingRequests.set(currentDomain, request$);
      return request$;
    }

    const cachedTheme = this.transferState.get(TRANSFER_STATE_KEYS.THEME_DATA, null);
    if (cachedTheme) {
      this.setColorsValues(cachedTheme);
      this.transferState.remove(TRANSFER_STATE_KEYS.THEME_DATA);
      return of(cachedTheme);
    }

    const path = `${this.apiUrl}/theme`;
    return this.http.get<Theme>(path).pipe(
      tap(res => {
        this.setColorsValues(res);
      }),
      catchError(error => {
        console.warn('Failed to fetch theme from API, using default theme:', error.message);
        this.setColorsValues(defaultTheme);
        return of(defaultTheme);
      })
    );
  }

  updateTheme(themeColors: Theme): Observable<ThemeColorsPayload> {
    const path = `${this.apiUrl}/backend/agency/theme`;
    return this.http
      .put<ThemeColorsPayload>(path, {
        text_color: themeColors.text_color,
        theme_color: themeColors.theme,
      })
      .pipe(
        tap(res => {
          this.setColorsValues(themeColors);
        })
      );
  }

  setColorsValues(themeColors: Theme): void {
    const renderer: Renderer2 = this.rendererFactory.createRenderer(null, null);
    // set cookie security params

    // Theme-color
    this.cookieStorageService.set('theme', themeColors.theme, {
      secure: true,
      sameSite: 'Lax',
    });
    renderer.setStyle(
      this.document.documentElement,
      '--main-color',
      themeColors.theme,
      RendererStyleFlags2.DashCase | RendererStyleFlags2.Important
    );
    // Text-color
    this.cookieStorageService.set('text-color', themeColors.text_color, {
      secure: true,
      sameSite: 'Lax',
    });
    renderer.setStyle(
      this.document.documentElement,
      '--text-color',
      themeColors.text_color,
      RendererStyleFlags2.DashCase | RendererStyleFlags2.Important
    );
  }

  getTheme(): Theme {
    return {
      theme: this.cookieStorageService.get('theme') as string,
      text_color: this.cookieStorageService.get('text-color') as string,
    };
  }

  private isBuildEnvironment(): boolean {
    // During build, both isBrowser and request are false
    // In SSR, isBrowser is false but request is defined
    return !this.isBrowser && !this.request;
  }
}
